import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, finalize, map, of, shareReplay, throwError } from 'rxjs';

import { environment } from '../../../environments/environment';
import { readApiString } from '../utils/api-response.util';
import { ApiUrlHelper } from '../config/api-url-helper';
import { StorageKey, StorageService } from './storage.service';
import type { AuthSession } from '../model/auth-model';

/** Statuses meaning the server looked at the refresh token and refused it. */
const SESSION_REJECTED_STATUSES = new Set([400, 401, 403]);

/**
 * Silently exchanges the refresh-token cookie for a new access token.
 * Concurrent callers share the same in-flight request so only one
 * refresh-token call ever goes out at a time.
 *
 * Emits the new access token, or null when the server rejected the refresh
 * token (the session is over). Errors instead when the refresh itself failed
 * (offline, 5xx, rate-limited) — that says nothing about the session, so
 * callers must not log the user out for it.
 */
@Injectable({ providedIn: 'root' })
export class TokenRefreshService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = inject(ApiUrlHelper);
  private readonly storage = inject(StorageService);

  private refresh$: Observable<string | null> | null = null;

  refreshAccessToken(): Observable<string | null> {
    if (this.refresh$) {
      return this.refresh$;
    }

    const base = environment.apiUrl.replace(/\/+$/, '');
    const path = this.apiUrl.apiUrl.account.refreshToken.replace(/^\/+/, '');
    const url = `${base}/${path}`;

    this.refresh$ = this.http.post<Record<string, unknown>>(url, {}, { withCredentials: true }).pipe(
      map((response) => {
        const success = Boolean(response?.['success'] ?? response?.['Success']);
        const data = (response?.['data'] ?? response?.['Data'] ?? {}) as Record<string, unknown>;
        const accessToken = readApiString(data, 'AccessToken');

        if (!success || !accessToken) {
          return null;
        }

        this.persistNewAccessToken(accessToken, readApiString(data, 'Expiry'));
        return accessToken;
      }),
      catchError((error: unknown) => {
        if (error instanceof HttpErrorResponse && SESSION_REJECTED_STATUSES.has(error.status)) {
          return of(null);
        }
        return throwError(() => error);
      }),
      shareReplay(1),
      finalize(() => {
        this.refresh$ = null;
      })
    );

    return this.refresh$;
  }

  private persistNewAccessToken(accessToken: string, expiry: string): void {
    const loginData = (this.storage.getValue(StorageKey.loginData) as Record<string, unknown>) ?? {};
    this.storage.setValue(StorageKey.loginData, { ...loginData, JWTToken: accessToken });

    const session = this.storage.getValue(StorageKey.authSession) as AuthSession | null;
    if (session) {
      this.storage.setValue(StorageKey.authSession, {
        ...session,
        accessToken,
        expiry: expiry || session.expiry,
      });
    }
  }
}
